import React, { useEffect, useMemo, useState } from "react";
import Card from "../../../components/Card";
import Loader from "../../../components/partials/components/loader";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { Col, Form, Row } from "react-bootstrap";
import { FiBarChart2, FiClock, FiSearch } from "react-icons/fi";
import { getApiCall } from "../../../service/httpCall";
import { API_AGENT_BREAK_EXCEEDED_STATS } from "../../../service/url";
import { toast } from "react-toastify";

const normalizeRows = (resp) => {
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp?.data)) return resp.data;
  if (Array.isArray(resp?.rows)) return resp.rows;
  return [];
};

const safeDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const formatISTDateTime = (value) => {
  const date = safeDate(value);
  if (!date) return "--";
  return date.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
};

const formatDateForCell = (value) => {
  const date = safeDate(value);
  if (!date) return { date: "--", time: "" };
  return {
    date: date.toLocaleDateString("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
    }),
    time: date.toLocaleTimeString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }),
  };
};

const formatDuration = (seconds) => {
  const s = Math.max(0, Number(seconds) || 0);
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const remSeconds = s % 60;
  const pad2 = (v) => String(v).padStart(2, "0");
  return `${pad2(hours)}:${pad2(minutes)}:${pad2(remSeconds)}`;
};

const getInitials = (name) => {
  const tokens = String(name || "")
    .trim()
    .split(" ")
    .filter(Boolean);
  if (!tokens.length) return "NA";
  if (tokens.length === 1) return tokens[0].slice(0, 2).toUpperCase();
  return `${tokens[0][0]}${tokens[1][0]}`.toUpperCase();
};

const MAX_FILTER_DAYS = 180;

const startOfDay = (value) => {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
};

const formatDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const toDateInputValue = (value) => {
  if (!value) return "";
  return formatDateInputValue(startOfDay(value));
};

const getDefaultDateRange = () => {
  const today = startOfDay(new Date());
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    from: monthStart,
    to: today,
  };
};

const getInclusiveDays = (startDate, endDate) => {
  const startMs = startOfDay(startDate).getTime();
  const endMs = startOfDay(endDate).getTime();
  return Math.floor((endMs - startMs) / (1000 * 60 * 60 * 24)) + 1;
};

const addDays = (date, days) => {
  const d = startOfDay(date);
  d.setDate(d.getDate() + days);
  return d;
};

function AgentBreakExceededList() {
  const defaultRange = getDefaultDateRange();
  const maxSelectableDate = startOfDay(new Date());
  const minSelectableDate = addDays(maxSelectableDate, -(MAX_FILTER_DAYS - 1));
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [fromDateValue, setFromDateValue] = useState(defaultRange.from);
  const [toDateValue, setToDateValue] = useState(defaultRange.to);

  const fetchExceededStats = ({ fromDate, toDate }) => {
    let isMounted = true;
    setLoading(true);

    const query = `?start_date=${encodeURIComponent(fromDate)}&end_date=${encodeURIComponent(toDate)}`;
    getApiCall(`${API_AGENT_BREAK_EXCEEDED_STATS}${query}`)
      .then((resp) => {
        if (!isMounted) return;
        setRows(normalizeRows(resp));
      })
      .catch((error) => {
        console.error("Failed to fetch exceeded break stats", error);
        if (!isMounted) return;
        setRows([]);
      })
      .finally(() => {
        if (!isMounted) return;
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  };

  useEffect(() => {
    const cleanup = fetchExceededStats({
      fromDate: toDateInputValue(defaultRange.from),
      toDate: toDateInputValue(defaultRange.to),
    });
    return cleanup;
  }, []);

  const tableRows = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    return list
      .map((r, idx) => {
        const startedAt = safeDate(r?.start_at);
        // Prefer end_at as source of truth until removed_at type is fully normalized in DB.
        const removedAt = safeDate(r?.end_at || r?.removed_at);
        return {
          key: r?.id || idx + 1,
          id: r?.id,
          agent_id: r?.user?.agent?.id || "--",
          agent_name: r?.user?.agent?.agent_name || "--",
          break_title: r?.break?.title || "--",
          break_type: r?.break?.type || "--",
          allowed_duration: Number(r?.break?.duration) || 0,
          started_at: formatISTDateTime(startedAt),
          removed_at: formatISTDateTime(removedAt),
          started_at_parts: formatDateForCell(startedAt),
          removed_at_parts: formatDateForCell(removedAt),
          exceeded_time: formatDuration(r?.exceeded_seconds),
          _startedTs: startedAt ? startedAt.getTime() : 0,
          _removedTs: removedAt ? removedAt.getTime() : 0,
        };
      })
      .sort((a, b) => (b._removedTs || 0) - (a._removedTs || 0));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const query = searchValue.trim().toLowerCase();

    return tableRows.filter((r) => {
      if (query) {
        const agentName = String(r?.agent_name || "").toLowerCase();
        const agentId = String(r?.agent_id || "").toLowerCase();
        const breakTitle = String(r?.break_title || "").toLowerCase();
        if (
          !agentName.includes(query) &&
          !agentId.includes(query) &&
          !breakTitle.includes(query)
        ) {
          return false;
        }
      }

      return true;
    });
  }, [searchValue, tableRows]);

  const validateAndFetch = () => {
    if (!fromDateValue || !toDateValue) {
      toast.error("Please select both start and end date.");
      return;
    }

    const startDate = startOfDay(fromDateValue);
    const endDate = startOfDay(toDateValue);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      toast.error("Invalid date selected.");
      return;
    }

    if (startDate > endDate) {
      toast.error("Start date cannot be after end date.");
      return;
    }

    const rangeDays = getInclusiveDays(startDate, endDate);
    if (rangeDays > MAX_FILTER_DAYS) {
      toast.error(`Date range too large. Maximum allowed is ${MAX_FILTER_DAYS} calendar days (inclusive).`);
      return;
    }

    fetchExceededStats({
      fromDate: toDateInputValue(fromDateValue),
      toDate: toDateInputValue(toDateValue),
    });
  };

  if (loading) return <Loader />;

  const breakTypeBodyTemplate = (rowData) => (
    <span
      style={{
        padding: "4px 10px",
        borderRadius: "999px",
        fontSize: "12px",
        fontWeight: 600,
        // background: "#e6f2ff",
        color: "#2f6fed",
        border: "1px solid #e6f2ff"
      }}
    >
      {rowData.break_type}
    </span>
  );

  const durationBodyTemplate = (rowData) => (
    <span
      style={{
        padding: "4px 10px",
        borderRadius: "999px",
        fontSize: "12px",
        fontWeight: 600,
        // background: "#e9f8f0",
        color: "#1c8b55",
        border: "1px solid #e6f2ff"
      }}
    >
      {rowData.allowed_duration} min
    </span>
  );

  const dateTimeBodyTemplate = (parts) => (
    <div>
      <div style={{ fontWeight: 600, color: "#2b2f36" }}>{parts.date}</div>
      {parts.time ? (
        <div style={{ fontSize: "12px", color: "#7a8699", marginTop: "2px" }}>{parts.time}</div>
      ) : null}
    </div>
  );

  const exceededBodyTemplate = (rowData) => (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        // background: "#fff1f2",
        color: "#d3304a",
        padding: "5px 10px",
        borderRadius: "999px",
        fontWeight: 600,
        border: "1px solid #e6f2ff"
      }}
    >
      <FiClock size={13} />
      <span>{rowData.exceeded_time}</span>
    </div>
  );

  const agentNameBodyTemplate = (rowData) => (
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      <div
        style={{
          width: "28px",
          height: "28px",
          borderRadius: "50%",
          background: "linear-gradient(135deg, #8b80f9 0%, #5f56da 100%)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: "11px",
        }}
      >
        {getInitials(rowData.agent_name)}
      </div>
      <span>{rowData.agent_name}</span>
    </div>
  );

  return (
    <Card className="shadow-sm border-0" style={{ padding: "10px" }}>
      <Card.Header
        className="d-flex align-items-center justify-content-start"
        style={{
         
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          // background: "#fff",
          width: "100%",
        }}
      >
        <div
          style={{
            
            fontWeight: 700,
            fontSize: 15,
            color: "var(--dash-text)",
            display: "flex",
            alignItems: "center",
            gap: 8,
            textAlign: "left",
            marginBottom:"6px"
          }}
        >
          <FiBarChart2 style={{ color: "var(--dash-primary)" }} />
          Agent Break Stats
        </div>
      </Card.Header>
      <Card.Body>
        <div
          style={{
            // background: "#fafbff",
            // border: "1px solid #7e7e7e",
            borderRadius: "12px",
            padding: "14px",
            marginBottom: "14px",
          }}
        >
          <Row className="g-0 align-items-center">
            <Col md={4} style={{ marginRight: "10px" }}>
              <div style={{ position: "relative" }}>
                <FiSearch
                  size={14}
                  style={{ position: "absolute", left: "11px", top: "50%", transform: "translateY(-50%)", color: "#9aa3b2" }}
                />
                <Form.Control
                  placeholder="Search by agent or break..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  style={{ paddingLeft: "34px", fontSize: "14px" }}
                />
              </div>
            </Col>
            <Col md={7}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div style={{ flex: 1 }}>
                  <Calendar
                    value={fromDateValue}
                    onChange={(e) => setFromDateValue(e.value ? startOfDay(e.value) : null)}
                    placeholder="Start Date"
                    dateFormat="yy-mm-dd"
                    minDate={minSelectableDate}
                    maxDate={toDateValue || defaultRange.to}
                    showIcon
                    showButtonBar
                    className="calender w-100"
                  />
                </div>
                <span style={{ color: "#8a94a6", fontWeight: 600, flexShrink: 0 }}>to</span>
                <div style={{ flex: 1 }}>
                  <Calendar
                    value={toDateValue}
                    onChange={(e) => setToDateValue(e.value ? startOfDay(e.value) : null)}
                    placeholder="End Date"
                    dateFormat="yy-mm-dd"
                    minDate={fromDateValue || minSelectableDate}
                    maxDate={maxSelectableDate}
                    showIcon
                    showButtonBar
                    className="calender w-100"
                  />
                </div>
                <Button
                  label="Filter"
                  onClick={validateAndFetch}
                  className="filter w-100"
                />
              </div>
            </Col>
            {/* <Col md={1} className="d-flex align-items-center">
              <Button
                label="Filter"
                onClick={validateAndFetch}
                className="filter w-100"
              />
            </Col> */}
          </Row>
        </div>

        <DataTable
          value={filteredRows}
          paginator
          rows={10}
          emptyMessage="No exceeded breaks found"
          className="p-datatable-sm"
          tableStyle={{ minWidth: "100%" }}
        >
          <Column field="agent_id" header="Agent ID" />
          <Column field="agent_name" header="Agent Name" body={agentNameBodyTemplate} />
          <Column field="break_title" header="Break Title" />
          <Column field="break_type" header="Break Type" body={breakTypeBodyTemplate} />
          <Column field="allowed_duration" header="Allowed Duration" body={durationBodyTemplate} />
          <Column
            field="started_at"
            header="Started At"
            body={(rowData) => dateTimeBodyTemplate(rowData.started_at_parts)}
          />
          <Column
            field="removed_at"
            header="Removed At"
            body={(rowData) => dateTimeBodyTemplate(rowData.removed_at_parts)}
          />
          <Column field="exceeded_time" header="Exceeded Time" body={exceededBodyTemplate} />
        </DataTable>
      </Card.Body>
    </Card>
  );
}

export default React.memo(AgentBreakExceededList);
